// src/pages/Register.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Container,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  useTheme,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Register = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, loading, signUpWithEmailPassword, initialAuthChecked } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    // Redirect if user becomes authenticated (e.g., after successful signup and auto-login, or manual login)
    // and the initial auth check has been completed.
    if (initialAuthChecked && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, initialAuthChecked, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (!email || !password) {
        setError('Email and password are required.');
        return;
    }

    const res = await signUpWithEmailPassword(email.trim(), password);

    if (res.success) {
      // res.data from authService.signUpWithEmailPassword contains { user, session } from Supabase
      if (res.data?.session) {
        // User is signed up AND a session is active (auto-logged in).
        // AuthContext's onAuthStateChange should pick this up, update the 'user' state,
        // which will then trigger the useEffect above for redirection.
        setMessage('Registration successful! Redirecting...');
        // Fields are not cleared because redirection should occur.
      } else if (res.data?.user) {
        // User object exists, but no session was returned immediately.
        // This typically means email confirmation is required.
        setMessage(
          'Registration successful! Please check your email to confirm your account.'
        );
        setEmail(''); // Clear fields as user needs to take further action
        setPassword('');
        setConfirmPassword('');
      } else {
        // This case implies success from Supabase but no user and no session data was returned.
        // This might happen if Supabase settings are very restrictive or change in the future.
        // Providing a general message for email checking is safest.
        setMessage('Registration acknowledged. Please check your email for next steps and to confirm your account.');
         setEmail('');
         setPassword('');
         setConfirmPassword('');
      }
    } else if (res.error) {
      // Use error message from Supabase if available
      setError(res.error?.message || (typeof res.error === 'string' ? res.error : 'Failed to register. Please try again.'));
    } else {
      setError('An unexpected error occurred during registration.');
    }
  };

  // Show loading spinner only during the submit operation itself (after initial page load check)
  const formSubmitting = loading && initialAuthChecked;
  // Page loading spinner for the very initial load before initialAuthChecked is true
  const pageInitialLoading = loading && !initialAuthChecked;


  if (pageInitialLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: theme.palette.background.default,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
      }}
    >
      <Container maxWidth="xs">
        <Paper sx={{ position: 'relative', p: 4, borderRadius: 2 }} elevation={4}>
          {formSubmitting && ( // Spinner for form submission in progress
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'rgba(255,255,255,0.7)',
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 2,
              }}
            >
              <CircularProgress />
            </Box>
          )}
          <Typography variant="h4" align="center" gutterBottom>
            Create Account
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
              {error}
            </Alert>
          )}
          {message && (
            <Alert severity={message.includes('Redirecting') ? 'success' : 'info'} sx={{ mb: 2 }}>
              {message}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              label="Email Address"
              name="email"
              type="email"
              required
              fullWidth
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ mb: 2 }}
              disabled={formSubmitting}
            />
            <TextField
              label="Password"
              name="password"
              type="password"
              required
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mb: 2 }}
              disabled={formSubmitting}
            />
            <TextField
              label="Confirm Password"
              name="confirmPassword"
              type="password"
              required
              fullWidth
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              sx={{ mb: 3 }}
              disabled={formSubmitting}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={formSubmitting || pageInitialLoading}
              size="large"
              sx={{ py: 1.5 }}
            >
              {formSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Register'}
            </Button>
          </Box>

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="body2">
              Already have an account?{' '}
              <Link to="/login" style={{ color: theme.palette.primary.main }}>
                Login here
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Register;